import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, Button, CircularProgress, Stack } from "@mui/material";
import {
  ContentLayout,
  DebouncedSearch,
  HeaderBreadcrumbs,
  ServerPaginatedTable,
  usePermission,
} from "../../../shared";
import { useCustomerSegmentation } from "../hooks";
import { Add } from "@mui/icons-material";

export const CustomerSegmentationList = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isStoreAdmin = pathname.includes("store-admin");
  const canCreate = usePermission("segment-modify");
  const canDelete = usePermission("segment-delete");

  const {
    state,
    breadcrumbLinks,
    gotoPage,
    setPageSize,
    onChangeFilter,
    toggleDeleteDialog,
  } = useCustomerSegmentation({});

  const basePath = isStoreAdmin
    ? "/store-admin/customer-segmentation"
    : "/customer-segmentation";

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: "No of Customers",
        accessor: "result_count",
        disableSortBy: true,
      },
      {
        Header: "Created By",
        accessor: (row) =>
          row.created_by?.name
            ? row.created_by.name
            : row.is_system
              ? "System"
              : "--",
        disableSortBy: true,
      },
      {
        Header: "Last Rerun",
        accessor: "last_updated_at",
        Cell: ({ value }) => value || "--",
        disableSortBy: true,
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        Cell: ({ row }) => (
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="flex-end"
          >
            {canDelete && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  toggleDeleteDialog(row.original);
                }}
                size="small"
                disabled={
                  row.original?.is_system ||
                  state.deleteSegment === row.original.id
                }
                sx={{ opacity: row.original?.is_system ? 0.5 : 1 }}
              >
                {state.deleteSegment === row.original.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <img alt="delete" src="/icons/ic_delete.svg" />
                )}
              </IconButton>
            )}
            <IconButton size="small">
              <img alt="details" src="/icons/arrow_forward.svg" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [basePath, state.deleteSegment],
  );

  return (
    <ContentLayout
      title="Customer Segments"
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
      rightContent={
        canCreate && (
          <Button
            variant="contained"
            onClick={() => navigate(`${basePath}/create`)}
            startIcon={<Add />}
          >
            New Segment
          </Button>
        )
      }
    >
      <ServerPaginatedTable
        columns={columns}
        data={state.segments ?? []}
        isLoading={state.tableLoading}
        searchBar={true}
        defaultSearch={false}
        pagination={state.pagination}
        gotoPage={gotoPage}
        setPageSize={setPageSize}
        manualSortBy={false}
        sortByGlobal={[]}
        filterContent={
          <DebouncedSearch
            value={state.search}
            onChange={onChangeFilter}
            placeholder="Search segments..."
          />
        }
        emptyMessage="No segments found"
        onRowLink={(row) => `${basePath}/details/${row.id}`}
      />
    </ContentLayout>
  );
};
